import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Snackbar } from '@elementary/common-ui/components';
import { useQuery } from '@tanstack/react-query';
import { INTEGRATION_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import * as api from 'shared/services/api';
import { useSlackIntegration } from 'shared/hooks/integrations';
import useNango from 'shared/hooks/useNango';
import useSnackbar from 'shared/hooks/useSnackbar';
import { IntegrationIssueSeverity, IntegrationType } from 'shared/types';

const DISMISS_STORAGE_PREFIX = 'IntegrationToastDismissed';

type IntegrationIssueProps = {
  id: string;
  description: string;
  severity: IntegrationIssueSeverity;
  integrationType: IntegrationType;
};

const IntegrationIssueToast = ({
  id,
  description,
  severity,
  integrationType,
}: IntegrationIssueProps) => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();
  const { slackIntegrationMutation } = useSlackIntegration({});
  const hasSubmittedReauth = useRef(false);

  const isSlackReauth = integrationType === 'slack' && severity === 'info';

  const dismissStorageKey = `${DISMISS_STORAGE_PREFIX}-${id}`;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(dismissStorageKey) === 'true',
  );

  const dismiss = useCallback(() => {
    sessionStorage.setItem(dismissStorageKey, 'true');
    setDismissed(true);
  }, [dismissStorageKey]);

  useEffect(() => {
    if (nangoSuccess && !hasSubmittedReauth.current) {
      hasSubmittedReauth.current = true;
      slackIntegrationMutation.mutate();
    }
  }, [nangoSuccess, slackIntegrationMutation]);

  useEffect(() => {
    if (nangoError || slackIntegrationMutation.isError) {
      openSnackbar({
        message: 'Failed to refresh your Slack connection. Please try again.',
        variant: 'error',
      });
    }
  }, [nangoError, slackIntegrationMutation.isError, openSnackbar]);

  const action = isSlackReauth
    ? { text: 'Add the agent to Slack', cb: () => nangoAuth('slack') }
    : {
        text: 'Go to settings',
        cb: () => navigate('/settings/environments'),
      };

  return (
    <Snackbar
      open={!dismissed}
      variant={severity}
      autoHide={false}
      message={description}
      actions={[action]}
      onClose={dismiss}
    />
  );
};

const IntegrationIssueToasts = () => {
  const hasAccountPermission = useAccountPermission();
  const { pathname } = useLocation();
  const canEditIntegrations = hasAccountPermission('EDIT_INTEGRATIONS');

  const isInEnvironmentsSettings = pathname.includes('/settings/environments');

  const { data: integrations } = useQuery(
    [INTEGRATION_OPTIONS],
    () => api.fetchAlertIntegrations(),
    { enabled: canEditIntegrations },
  );

  const toasts = useMemo<IntegrationIssueProps[]>(
    () =>
      (integrations ?? []).flatMap((integration) =>
        (integration.issues ?? []).map((issue) => ({
          id: integration.id + issue.id,
          description: issue.description,
          severity: issue.severity,
          integrationType: integration.type,
        })),
      ),
    [integrations],
  );

  if (isInEnvironmentsSettings) {
    return null;
  }

  return toasts?.map((toast) => (
    <IntegrationIssueToast key={toast.id} {...toast} />
  ));
};

export default IntegrationIssueToasts;
